import React, { useState, useEffect } from 'react';

interface FusedFormProps {
    COLORS: any;
    FONT_FAMILY: string;
    onModelComplete?: (bucketPath: string) => void;
    onFormParamsChange?: (params: { year: number; month: number; day: number; forecast_shift_hrs: number }) => void;
}

interface FusedParams {
    day: number;
    month: number;
    year: number;
    forecast_shift_hrs: number;
    gcs_path: string;
    bucket_name: string;
    bucket_namespace_base: string;
}

export const FusedForm: React.FC<FusedFormProps> = ({ COLORS, FONT_FAMILY, onModelComplete, onFormParamsChange }) => {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() - 4);

    const [params, setParams] = useState<FusedParams>({
        day: defaultDate.getDate(),
        month: defaultDate.getMonth() + 1,
        year: defaultDate.getFullYear(),
        forecast_shift_hrs: 30,
        gcs_path: 'data/sunpde_output/prod/test_3/targets/',
        bucket_name: 'local',
        bucket_namespace_base: 'data/sunpde_output/mcp_output'
    });

    // Latest available date from LISA (caps the date picker)
    const [latestAvailable, setLatestAvailable] = useState<{ year: number; month: number; day: number } | null>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [startTime, setStartTime] = useState<number | null>(null);
    const [currentRunIdentifier, setCurrentRunIdentifier] = useState<string>('');

    // Fetch the latest date with available LISA data on mount
    useEffect(() => {
        fetch('/inference/latest-date')
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch latest date');
                return res.json();
            })
            .then((data: { year: number; month: number; day: number }) => {
                console.log('Latest available LISA date:', data);
                setLatestAvailable(data);
                setParams(prev => ({
                    ...prev,
                    year: data.year,
                    month: data.month,
                    day: data.day,
                }));
                if (onFormParamsChange) {
                    onFormParamsChange({
                        year: data.year,
                        month: data.month,
                        day: data.day,
                        forecast_shift_hrs: params.forecast_shift_hrs,
                    });
                }
            })
            .catch(err => {
                console.warn('Could not fetch latest LISA date, using fallback:', err);
            });
    }, []);

    // Generate run_identifier using the same format as fast.py
    const generateRunIdentifier = (year: number, month: number, day: number, forecast_shift_hrs: number): string => {
        const randomSuffix = Math.random().toString(36).substring(2, 8); // 6 random alphanumeric characters
        return `fdlmcp_${year}_${month}_${day}_${forecast_shift_hrs}_${randomSuffix}`;
    };

    // Get the number of days in a given month and year (accounting for leap years)
    const getDaysInMonth = (year: number, month: number): number => {
        return new Date(year, month, 0).getDate();
    };

    // Check if the selected date is valid (not beyond the latest available LISA date)
    const isDateValid = (year: number, month: number, day: number): boolean => {
        if (!latestAvailable) return true; // Allow any date if we haven't fetched yet
        const selectedDate = new Date(year, month - 1, day);
        const maxDate = new Date(latestAvailable.year, latestAvailable.month - 1, latestAvailable.day, 23, 59, 59, 999);
        return selectedDate <= maxDate;
    };

    // Initialize form parameters when component mounts
    useEffect(() => {
        if (onFormParamsChange) {
            onFormParamsChange({
                year: params.year,
                month: params.month,
                day: params.day,
                forecast_shift_hrs: params.forecast_shift_hrs
            });
        }
        // Generate initial run identifier
        setCurrentRunIdentifier(generateRunIdentifier(params.year, params.month, params.day, params.forecast_shift_hrs));
    }, []); // Empty dependency array means this runs once on mount

    // Update run identifier when date parameters change
    useEffect(() => {
        setCurrentRunIdentifier(generateRunIdentifier(params.year, params.month, params.day, params.forecast_shift_hrs));
    }, [params.year, params.month, params.day, params.forecast_shift_hrs]);

    // Validate day when month or year changes
    useEffect(() => {
        const maxDays = getDaysInMonth(params.year, params.month);
        if (params.day > maxDays) {
            setParams(prev => ({ ...prev, day: maxDays }));
        }
    }, [params.year, params.month]);

    // Debounced validation for forecast shift hours
    const [forecastShiftTimeout, setForecastShiftTimeout] = useState<NodeJS.Timeout | null>(null);
    const [forecastShiftInput, setForecastShiftInput] = useState(params.forecast_shift_hrs.toString());

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (forecastShiftTimeout) {
                clearTimeout(forecastShiftTimeout);
            }
        };
    }, [forecastShiftTimeout]);

    // Debug initial state
    useEffect(() => {
        console.log('Initial forecastShiftInput:', forecastShiftInput);
        console.log('Initial params.forecast_shift_hrs:', params.forecast_shift_hrs);
    }, []);

    // Sync input state when params change (but only if not from our own validation)
    useEffect(() => {
        if (params.forecast_shift_hrs !== parseInt(forecastShiftInput) && forecastShiftInput !== '') {
            console.log('Syncing input state to params:', params.forecast_shift_hrs);
            setForecastShiftInput(params.forecast_shift_hrs.toString());
        }
    }, [params.forecast_shift_hrs]);

    const handleForecastShiftChange = (value: string) => {
        console.log('Forecast shift input changed to:', value);

        // Clear any existing timeout
        if (forecastShiftTimeout) {
            clearTimeout(forecastShiftTimeout);
        }

        // Update the input value immediately for responsive typing
        // But don't allow values above 30 to be displayed
        if (value === '' || parseInt(value) <= 30) {
            setForecastShiftInput(value);
        }

        // Set a new timeout to validate after 1 second of no typing
        const timeout = setTimeout(() => {
            console.log('Validating forecast shift value:', value);

            if (value === '') {
                // No validation needed for empty input
                console.log('Empty input - no validation needed');
                return;
            }

            const numValue = parseInt(value);
            if (!isNaN(numValue)) {
                const validatedValue = Math.min(Math.max(numValue, 1), 30);
                console.log('Validated value:', validatedValue);

                // Update with validated value
                setParams(prev => ({
                    ...prev,
                    forecast_shift_hrs: validatedValue
                }));

                // Call onFormParamsChange with validated value
                if (onFormParamsChange) {
                    onFormParamsChange({
                        year: params.year,
                        month: params.month,
                        day: params.day,
                        forecast_shift_hrs: validatedValue
                    });
                }
            }
        }, 1000);

        setForecastShiftTimeout(timeout);
    };

    const handleParamChange = (key: keyof FusedParams, value: string | number) => {
        let newValue = typeof value === 'string' && key !== 'gcs_path' ? parseInt(value) || 0 : value;

        // Validate date parameters
        if (key === 'year' || key === 'month' || key === 'day') {
            const newYear = key === 'year' ? newValue as number : params.year;
            const newMonth = key === 'month' ? newValue as number : params.month;
            const newDay = key === 'day' ? newValue as number : params.day;

            // Check if the new date would be valid
            if (!isDateValid(newYear, newMonth, newDay) && latestAvailable) {
                console.warn(`Date ${newYear}-${newMonth}-${newDay} exceeds latest available LISA data. Capping to ${latestAvailable.year}-${latestAvailable.month}-${latestAvailable.day}`);

                setParams(prev => ({
                    ...prev,
                    year: latestAvailable.year,
                    month: latestAvailable.month,
                    day: latestAvailable.day
                }));
                return;
            }
        }



        const newParams = {
            ...params,
            [key]: newValue
        };
        setParams(newParams);

        // Call onFormParamsChange with the updated form parameters
        if (onFormParamsChange) {
            onFormParamsChange({
                year: newParams.year,
                month: newParams.month,
                day: newParams.day,
                forecast_shift_hrs: newParams.forecast_shift_hrs
            });
        }
    };

    // Inference server URL - always use relative path (proxied through nginx)
    const INFERENCE_URL = '/inference/run-model';

    const handleRun = async () => {
        setIsLoading(true);
        setError('');
        setResponse('');
        setShowSuccessMessage(false);
        setElapsedSeconds(0);
        setStartTime(Date.now());

        // Start timer
        const timer = setInterval(() => {
            setElapsedSeconds(prev => prev + 1);
        }, 1000);

        try {
            console.log('Calling local inference server:', INFERENCE_URL);

            const response = await fetch(INFERENCE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    day: params.day,
                    month: params.month,
                    year: params.year,
                    forecast_shift_hrs: params.forecast_shift_hrs,
                    run_identifier: currentRunIdentifier,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const data = await response.json();
            setResponse(JSON.stringify(data, null, 2));

            // Extract bucket_namespace from response and call onModelComplete
            if (data && data.bucket_namespace) {
                const bucketPath = `${data.bucket_namespace}/preds`;
                if (onModelComplete) {
                    onModelComplete(bucketPath);
                }
                setShowSuccessMessage(true);
                // Hide success message after 5 seconds
                setTimeout(() => setShowSuccessMessage(false), 5000);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
            setStartTime(null);
            clearInterval(timer);
        }
    };

    return (
        <div style={{
            padding: '20px',
            background: COLORS.bottomRightPane,
            borderRadius: '8px',
            marginBottom: '20px',
            border: `1px solid ${COLORS.focusBorder}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>

            <h3 style={{
                color: COLORS.text,
                fontFamily: FONT_FAMILY,
                marginBottom: '16px',
                fontSize: '18px',
                fontWeight: '600'
            }}>
                Run model
            </h3>

            {showSuccessMessage && (
                <div style={{
                    background: '#4CAF50',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    fontFamily: FONT_FAMILY,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    ✅ Model output was passed to render
                </div>
            )}

            {latestAvailable && (
                <div style={{
                    fontSize: '12px',
                    color: COLORS.text,
                    opacity: 0.7,
                    marginBottom: '12px',
                    fontFamily: FONT_FAMILY
                }}>
                    Latest available LISA data: {latestAvailable.year}-{String(latestAvailable.month).padStart(2, '0')}-{String(latestAvailable.day).padStart(2, '0')}
                </div>
            )}

            <div style={{ display: 'grid', gap: '12px', marginBottom: '16px', width: '100%' }}>
                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Day:
                    </label>
                    <select
                        value={params.day}
                        onChange={(e) => handleParamChange('day', parseInt(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    >
                        {Array.from({ length: getDaysInMonth(params.year, params.month) }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day} disabled={!isDateValid(params.year, params.month, day)}>{day}</option>
                        ))}
                    </select>

                </div>

                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Month:
                    </label>
                    <select
                        value={params.month}
                        onChange={(e) => handleParamChange('month', parseInt(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                            <option key={month} value={month} disabled={!isDateValid(params.year, month, 1)}>
                                {new Date(2024, month - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Year:
                    </label>
                    <select
                        value={params.year}
                        onChange={(e) => handleParamChange('year', parseInt(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    >
                        {Array.from({ length: (latestAvailable?.year ?? new Date().getFullYear()) - 2010 + 1 }, (_, i) => 2010 + i).map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>

                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Forecast Shift (hours):
                    </label>
                    <input
                        type="number"
                        value={forecastShiftInput}
                        onChange={(e) => handleForecastShiftChange(e.target.value)}
                        min="1"
                        max="30"
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    />
                    <div style={{
                        fontSize: '12px',
                        color: COLORS.text,
                        opacity: 0.7,
                        marginTop: '4px',
                        fontFamily: FONT_FAMILY
                    }}>
                        Range: 1 - 30 hours
                    </div>
                </div>

                {/* <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        GCS Path:
                    </label>
                    <input
                        type="text"
                        value={params.gcs_path}
                        onChange={(e) => handleParamChange('gcs_path', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Bucket Name:
                    </label>
                    <input
                        type="text"
                        value={params.bucket_name}
                        onChange={(e) => handleParamChange('bucket_name', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    />
                </div>

                <div style={{ width: '100%' }}>
                    <label style={{
                        display: 'block',
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        fontSize: '14px',
                        marginBottom: '4px'
                    }}>
                        Bucket Namespace Base:
                    </label>
                    <input
                        type="text"
                        value={params.bucket_namespace_base}
                        onChange={(e) => handleParamChange('bucket_namespace_base', e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: `1px solid ${COLORS.focusBorder}`,
                            borderRadius: '4px',
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: FONT_FAMILY,
                            fontSize: '14px',
                            boxSizing: 'border-box'
                        }}
                    />
                </div> */}
            </div>

            <button
                onClick={handleRun}
                disabled={isLoading}
                style={{
                    background: isLoading ? '#666' : COLORS.focusBorder,
                    color: COLORS.background,
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 20px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    fontFamily: FONT_FAMILY,
                    fontSize: '14px',
                    fontWeight: '600',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    position: 'relative'
                }}
                title={isLoading ? `Running for ${elapsedSeconds} seconds` : undefined}
            >
                {isLoading && (
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: `2px solid ${COLORS.background}`,
                        borderTop: `2px solid transparent`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                )}
                {isLoading ? 'Running...' : 'Run'}
                {isLoading && (
                    <span style={{
                        marginLeft: '8px',
                        fontSize: '12px',
                        opacity: 0.8
                    }}>
                        ({elapsedSeconds}s)
                    </span>
                )}
            </button>

            {/* Show run identifier underneath after run is clicked */}
            {(isLoading || response || error) && (
                <div style={{
                    fontSize: '12px',
                    color: COLORS.text,
                    opacity: 0.5,
                    marginBottom: '16px',
                    fontFamily: 'monospace',
                    textAlign: 'center'
                }}>
                    Run ID: {currentRunIdentifier}
                </div>
            )}

            {error && (
                <div style={{
                    background: '#ff4444',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    fontFamily: FONT_FAMILY,
                    fontSize: '14px'
                }}>
                    Error: {error}
                </div>
            )}

            {response && (
                <div>
                    <h4 style={{
                        color: COLORS.text,
                        fontFamily: FONT_FAMILY,
                        marginBottom: '8px',
                        fontSize: '16px',
                        fontWeight: '600'
                    }}>
                        Response:
                    </h4>
                    <pre style={{
                        background: COLORS.background,
                        color: COLORS.text,
                        padding: '12px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '300px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        border: `1px solid ${COLORS.focusBorder}`
                    }}>
                        {response}
                    </pre>
                </div>
            )}
        </div>
    );
}; 